import axios from 'axios';

import { IProduct } from '@/components/pdp/IProduct';
import {
    convertPrice,
    digestMessage,
    generateRandomId,
    getBaseUrl,
    getCookie,
} from '@/components/tracking_third_party/utils/fbcap.utils';
import { isConsentCategoryDisabled } from '@/tscapi/utils/consent';
import { Cookie } from '@/tscapi/utils/cookie';
declare global {
    interface Window {
        ttq: any;
        fbq: any;
        _fbq: any;
    }
}

type TiktokObject = {
    event_source: string;
    event_source_id: string; //pixel_code
    test_event_code?: string;
    data: {
        event: string;
        event_time: string;
        event_id: string; //orderId
        properties: {
            order_id?: string;
            content_type: string;
            contents: {
                price: number;
                quantity: number;
                content_id: string;
                content_category?: string;
                content_name: string;
                brand: string;
            }[];
            currency: string;
            value: number; //total value of the order
            description?: string;
            event_id?: string;
        };
        user: {
            ttclid: string; //hashed click id
            email?: string; //hashed email
            phone?: string;
            external_id?: string; //edpno
            ttp: string; // _ttp cookie
            ip: string;
            user_agent: string;
            locale: string;
        };
        page: {
            url: string;
            referrer: string;
        };
    }[];
};

type ViewContentEventDetail = {
    productData: IProduct;
    selectedEdp: number;
};

document.addEventListener(
    'viewContentEvent',
    function (e: CustomEvent<ViewContentEventDetail>) {
        ttViewContentEvent(e.detail);
    },
    false,
);

async function ttViewContentEvent(eventDetails: ViewContentEventDetail) {
    //if consent is not given, prevent tt event
    if (isConsentCategoryDisabled('targeting')) {
        return;
    }
    if (!eventDetails.productData.ItemNo) return;

    const currentHostname = window.location.hostname;
    const url = getBaseUrl(currentHostname);
    const randomEventNumber = generateRandomId(15);
    const tiktokObject = await generateTiktokObject(eventDetails, randomEventNumber);

    const axiosInstance = axios.create({
        baseURL: url,
        withCredentials: true,
        headers: {
            Authorization: 'Basic 567e407f-4bc5-463e-9c28-3ac2a2b333ec',
            'Content-Type': 'application/json',
        },
    });

    // server event
    if (window.tscConfig?.CapiEnabled == 'true') {
        try {
            await axiosInstance.post('/tracking/v1/events/', tiktokObject);
        } catch (error) {
            console.log('Error sending TT view content server event:', error?.response?.data || error.message);
        }
    }

    // pixel event
    try {
        window.ttq.track('ViewContent', tiktokObject[0].eventData.data[0].properties, {
            event_id: randomEventNumber,
        });
    } catch (e) {
        console.log('Error sending TT view content pixel event');
    }
}

async function generateTiktokObject(eventData: ViewContentEventDetail, randomEventNumber) {
    const ttclid = getCookie('ttclid'); // TikTok Click ID
    const ttp = getCookie('_ttp'); //Cookie i
    const cookie = new Cookie();
    const productLine = eventData.productData.Edps.find((product) => product.EdpNo === eventData.selectedEdp);
    const tiktokObject: TiktokObject = {
        event_source: 'web',
        event_source_id: 'C8AKQ23G5HF9JD2HK3O0', //tt pixel code
        data: [
            {
                event: 'ViewContent',
                event_id: randomEventNumber,
                event_time: Math.floor(Date.now() / 1000).toString(),
                properties: {
                    content_type: 'product_group',
                    contents: productLine
                        ? [
                              {
                                  content_id: productLine.ItemNo,
                                  price: convertPrice(productLine.AppliedPrice),
                                  quantity: 1,
                                  content_category:
                                      eventData.productData.ProductTypes && eventData.productData.ProductTypes[0]
                                          ? eventData.productData.ProductTypes[0]
                                          : '',
                                  content_name: eventData.productData.Name,
                                  brand: eventData.productData.Brand,
                              },
                          ]
                        : [],

                    currency: 'CAD',
                    value: convertPrice(productLine?.AppliedPrice || '0'),
                },
                user: {
                    ttclid: ttclid ?? '',
                    ttp: ttp,
                    ip: cookie?.TscClientIP ?? '',
                    user_agent: window.navigator.userAgent || '',
                    locale: cookie?.Language.replace('_', '-'), // Modified BCP 47 language identifier
                },
                page: {
                    url: window.location.href,
                    referrer: window.location.host,
                },
            },
        ],
    };
    // If user is signed in, add user data
    if (cookie.UserAccessToken?.Token?.length > 0 && cookie.UserAccessToken.Edp > 0) {
        if (cookie.UserEmail) {
            tiktokObject.data[0].user.email = await digestMessage(cookie.UserEmail.toLowerCase());
        }
    }
    //add test code if exits
    if (window.tscConfig?.TiktokTestCode) {
        tiktokObject.test_event_code = window.tscConfig.TiktokTestCode;
    }

    return [{ eventConsumer: 'tiktok', eventData: { ...tiktokObject } }];
}
